import ChevronLeftIcon from '@material-design-icons/svg/filled/chevron_left.svg?react';
import ChevronRightIcon from '@material-design-icons/svg/filled/chevron_right.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import type { ComponentType, ReactNode } from 'react';
import { formatRelativeTime } from '../../lib/formatters';
import { cn } from '../../lib/utils';
import { formatDateTimeWithLocalZone } from '../../utils/stringHelpers';

/**
 * The list container used by the admin list pages: a count header with a
 * refresh control, the rows (table on desktop, row list on phones), an empty
 * state, and a range pager. Same shell as the tickets queue.
 */

export interface ListPagination {
  currentPage: number;
  totalPages: number;
  total: number;
}

export function ListShell({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900/60',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function ListHeader({
  total,
  noun,
  isLoading,
  onReload,
  actions,
}: {
  total: number;
  /** Singular and plural, e.g. `['dapp', 'dapps']`. */
  noun: [string, string];
  isLoading: boolean;
  onReload: () => void;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-neutral-800 px-3 py-2 sm:px-4">
      <h2 className="text-xs font-medium text-neutral-400">
        {isLoading && total === 0 ? (
          <span
            className="inline-block h-3 w-16 animate-pulse rounded bg-neutral-700/70 align-middle"
            aria-hidden="true"
          />
        ) : (
          <>
            <span className="text-white">{total.toLocaleString()}</span>{' '}
            {total === 1 ? noun[0] : noun[1]}
          </>
        )}
      </h2>
      <div className="flex items-center gap-2">
        {actions}
        <button
          type="button"
          onClick={onReload}
          disabled={isLoading}
          aria-label="Refresh"
          title="Refresh"
          className="inline-flex size-7 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshIcon
            className={cn('size-4 fill-current', isLoading && 'animate-spin')}
          />
        </button>
      </div>
    </div>
  );
}

export function ListPager({
  pagination,
  pageSize,
  onPageChange,
}: {
  pagination: ListPagination;
  pageSize: number;
  onPageChange: (page: number) => void;
}) {
  const { currentPage, totalPages, total } = pagination;

  if (total === 0) {
    return null;
  }

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, total);
  const buttonClassName =
    'inline-flex size-8 items-center justify-center rounded-md text-neutral-300 transition-colors hover:bg-neutral-800 hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent';

  return (
    <div className="flex items-center justify-between gap-3 border-t border-neutral-800 px-3 py-1.5 text-xs text-neutral-400 sm:px-4">
      <span className="tabular-nums">
        {start}–{end} of {total.toLocaleString()}
      </span>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            aria-label="Previous page"
            className={buttonClassName}
          >
            <ChevronLeftIcon className="size-5 fill-current" />
          </button>
          <span className="tabular-nums">
            {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            aria-label="Next page"
            className={buttonClassName}
          >
            <ChevronRightIcon className="size-5 fill-current" />
          </button>
        </div>
      )}
    </div>
  );
}

export function ListEmptyState({
  icon: Icon,
  title,
  hint,
  hasActiveFilters,
  onClearFilters,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  hint: string;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-neutral-800">
        <Icon className="size-6 fill-current text-neutral-500" />
      </div>
      <div className="text-sm font-medium text-white">
        {hasActiveFilters ? 'Nothing matches' : title}
      </div>
      <p className="max-w-xs text-xs text-neutral-400">
        {hasActiveFilters ? 'Try another segment or clear the filters.' : hint}
      </p>
      {hasActiveFilters && (
        <div className="text-xs">
          <button
            type="button"
            onClick={onClearFilters}
            className="rounded-md border border-neutral-700 px-3 py-1.5 font-medium text-neutral-200 transition-colors hover:bg-neutral-800"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}

function RowSkeleton() {
  return (
    <div
      className="flex items-start gap-3 px-3 py-3 sm:px-4"
      aria-hidden="true"
    >
      <div className="mt-0.5 size-10 shrink-0 animate-pulse rounded-lg bg-neutral-800" />
      <div className="flex-1 space-y-2">
        <div className="flex justify-between gap-3">
          <div className="h-3.5 w-2/5 animate-pulse rounded bg-neutral-800" />
          <div className="h-3 w-8 animate-pulse rounded bg-neutral-800" />
        </div>
        <div className="h-3 w-1/2 animate-pulse rounded bg-neutral-800" />
        <div className="flex gap-1.5">
          <div className="h-5 w-16 animate-pulse rounded-md bg-neutral-800" />
          <div className="h-5 w-20 animate-pulse rounded-md bg-neutral-800" />
        </div>
      </div>
    </div>
  );
}

/**
 * Row list for phones. Each row is one button; `renderTrailing` renders a
 * sibling of that button (never inside it) for per-row actions.
 */
export function RowList<T>({
  items,
  getId,
  selectedId,
  isLoading,
  onSelect,
  renderRow,
  renderTrailing,
}: {
  items: T[];
  getId: (item: T) => string;
  selectedId?: string | null;
  isLoading: boolean;
  onSelect: (item: T) => void;
  renderRow: (item: T) => ReactNode;
  renderTrailing?: (item: T) => ReactNode;
}) {
  if (isLoading && items.length === 0) {
    return (
      <div className="divide-y divide-neutral-800/80">
        {Array.from({ length: 8 }).map((_, index) => (
          <RowSkeleton key={index} />
        ))}
      </div>
    );
  }

  return (
    <ul
      className={cn(
        'divide-y divide-neutral-800/80 transition-opacity',
        isLoading && 'opacity-60',
      )}
    >
      {items.map((item) => {
        const id = getId(item);
        const isSelected = id === selectedId;
        const trailing = renderTrailing?.(item);
        return (
          <li key={id} className="flex items-stretch">
            <button
              type="button"
              onClick={() => onSelect(item)}
              aria-current={isSelected ? 'true' : undefined}
              className={cn(
                'flex min-w-0 flex-1 items-start gap-3 border-l-2 px-3 py-3 text-left transition-colors sm:px-4',
                isSelected
                  ? 'border-teal-400 bg-teal-500/[0.08]'
                  : 'border-transparent hover:bg-white/[0.03] active:bg-white/[0.05]',
              )}
            >
              {renderRow(item)}
            </button>
            {trailing && (
              <div className="flex shrink-0 items-center pr-3 sm:pr-4">
                {trailing}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function RelativeTime({
  timestamp,
  className,
}: {
  timestamp: number;
  className?: string;
}) {
  return (
    <time
      dateTime={new Date(timestamp).toISOString()}
      title={formatDateTimeWithLocalZone(timestamp)}
      className={cn('whitespace-nowrap tabular-nums', className)}
    >
      {formatRelativeTime(timestamp)}
    </time>
  );
}

export const tableHeaderClassName =
  'text-[11px] font-semibold uppercase tracking-wide text-neutral-400';

/** Column shorthand for `DataTable`, matching the tickets table's density. */
export function listColumn<T>(
  key: string,
  header: ReactNode,
  size: number,
  options: { sortable?: boolean; descFirst?: boolean; className?: string },
  accessor: (row: T) => ReactNode,
) {
  return {
    key,
    header,
    headerClassName: tableHeaderClassName,
    className: cn('!px-4', options.className),
    size,
    enableSorting: options.sortable ?? false,
    sortDescFirst: options.descFirst,
    accessor,
  };
}
